/* SLOPCORE STORE (2026-10-04): the 360s, the product form and the bag. No dependencies. */
(() => {
  'use strict';
  const SC = window.SC || {};
  const root = SC.root || '/';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* money in the store's format, without trailing zeros ($35, $35.50) */
  const money = (cents) => {
    const fmt = SC.money || '${{amount}}';
    const v = (Number(cents) || 0) / 100;
    const whole = Math.round(v * 100) % 100 === 0;
    const plain = v.toLocaleString('en-US', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 });
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) =>
      k === 'amount_with_comma_separator' ? plain.replace(/,/g, '.').replace(/\.(\d{2})$/, ',$1')
        : k === 'amount_no_decimals' ? Math.round(v).toLocaleString('en-US') : plain);
  };
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /* ---------------------------------------------------------------- header hairline */
  const hdr = document.querySelector('[data-hdr]');
  const shipbar = document.querySelector('.shipbar');   /* the header only sticks once the free-shipping bar has scrolled away */
  const onScrollHdr = () => hdr && hdr.classList.toggle('is-stuck', window.scrollY > (shipbar ? shipbar.offsetHeight : 0) + 4);
  window.addEventListener('scroll', onScrollHdr, { passive: true });
  onScrollHdr();

  /* ---------------------------------------------------------------- the 360s
     Each spin is the untouched provider rotation, twice over. At rest the video itself plays (spin-<key>.mp4, every frame
     at 24 fps, one turn in 12 s), as smooth as the original: the user, 2026-10-04: "it should be as smooth as the og video
     ... i only want that [the frames] to be drag behavior". 96 of its frames on a canvas take over whenever it is turned:
     scrolling turns it (one turn per ~0.95 viewport; frames crossfade, so it reads as motion) and dragging turns it,
     showing one whole frame, which it settles on and holds for HOLD_MS after release, so the print reads where it stops.
     Then the video picks up from that frame. Without the video (still loading, autoplay refused, reduced motion) the
     frames keep turning slowly by themselves. */
  const spins = [];
  const IDLE_TURN_S = 12.5;      // the frames' own slow turn, only while the video can't play
  const HOLD_MS = 5000;          // 2026-10-04, the user: "ideally we could make it so the text is clear at any released
                                 // point after grabbing and rotating" (was a 1.2 s pause, mid-blend)
  const SCROLL_REST_MS = 160;    // the video resumes this long after the last scroll
  const FRAME_DT = 0.125;        // seconds of video per canvas frame: frame i is video frame 3i of 288 at 24 fps

  class Spin {
    constructor(el) {
      this.el = el;
      this.cv = el.querySelector('canvas');
      this.cx = this.cv.getContext('2d', { alpha: false });
      try { this.urls = JSON.parse(el.querySelector('[data-spin-frames]').textContent); } catch (e) { this.urls = []; }
      this.n = this.urls.length;
      this.imgs = new Array(this.n);
      this.ok = new Array(this.n).fill(false);
      this.a = 0;
      this.key = null;
      this.visible = false;      // near the viewport: frames load
      this.onscreen = false;     // in the viewport: the video may play
      this.loading = false;
      this.hold = 0;
      this.lastScroll = -1e9;
      this.video = el.querySelector('video');
      this.noVideo = !this.video || !this.video.dataset.src || reduce;
      this.playing = false;      // the video is what's on screen
      this.pending = false;      // a seek-then-play is under way
      this.hudDeg = -1;
      this.deg = el.querySelector('[data-spin-deg]');
      this.bar = el.querySelector('[data-spin-bar]');
      this.cx.fillStyle = getComputedStyle(el).getPropertyValue('--tile') || '#ececec';
      this.cx.fillRect(0, 0, this.cv.width, this.cv.height);
      if (el.classList.contains('spin--drag')) this.bindDrag();
    }
    load() {
      if (!this.noVideo && !this.video.getAttribute('src')) {
        this.video.muted = true;
        this.video.preload = 'auto';
        this.video.src = this.video.dataset.src;
      }
      if (this.loading || !this.n) return;
      this.loading = true;
      const order = [];
      const seen = new Set();
      for (const step of [this.n, 24, 12, 6, 3, 1]) {
        for (let i = 0; i < this.n; i += step) if (!seen.has(i)) { seen.add(i); order.push(i); }
      }
      let k = 0;
      const next = () => {
        if (k >= order.length) return;
        const i = order[k++];
        const im = new Image();
        im.decoding = 'async';
        im.onload = () => { this.ok[i] = true; this.key = null; next(); };
        im.onerror = next;
        im.src = this.urls[i];
        this.imgs[i] = im;
      };
      for (let c = 0; c < 6; c++) next();
    }
    nearest(i) {
      if (this.ok[i]) return i;
      for (let d = 1; d < this.n; d++) {
        const b = (i - d + this.n) % this.n;
        if (this.ok[b]) return b;
        const a = (i + d) % this.n;
        if (this.ok[a]) return a;
      }
      return -1;
    }
    hud(shown) {
      const n = this.n;
      const x = ((shown % n) + n) % n;
      const deg = Math.floor((x / n) * 360) % 360;
      if (deg === this.hudDeg) return;
      this.hudDeg = deg;
      if (this.deg) this.deg.textContent = String(deg).padStart(3, '0') + '°';
      if (this.bar) this.bar.style.transform = 'scaleX(' + (x / n).toFixed(4) + ')';
    }
    draw(now) {
      if (!this.n) return;
      const n = this.n;
      const a = ((this.a % n) + n) % n;
      const whole = this.dragging || now < this.hold;          // dragged or resting after a drag: one whole frame
      const i = whole ? Math.round(a) % n : Math.floor(a);
      const f = whole ? 0 : a - i;
      const key = i * 16 + Math.round(f * 12);
      if (key === this.key) return;
      this.key = key;
      const j = (i + 1) % n;
      const fi = this.nearest(i);
      if (fi < 0) return;
      const { cx, cv } = this;
      cx.globalAlpha = 1;
      cx.drawImage(this.imgs[fi], 0, 0, cv.width, cv.height);
      if (f > 0.04 && fi === i && this.ok[j]) {
        cx.globalAlpha = f;
        cx.drawImage(this.imgs[j], 0, 0, cv.width, cv.height);
        cx.globalAlpha = 1;
      }
      this.hud(whole ? i : a);
    }
    /* hand over from the video to the frames, at the angle the video has reached */
    toFrames() {
      this.pending = false;
      if (!this.playing) return;
      this.playing = false;
      const v = this.video;
      this.a = v.currentTime / FRAME_DT;
      v.pause();
      this.key = null;
      this.draw(performance.now());                             // the canvas shows where the video was...
      this.el.classList.remove('is-video');                     // ...before the video goes
    }
    /* hand back to the video from the frame on the canvas; false while the video isn't ready */
    toVideo() {
      if (this.playing || this.pending || this.noVideo || !this.onscreen) return this.pending;
      const v = this.video;
      if (v.readyState < 2) return false;
      const n = this.n;
      const i = Math.round(((this.a % n) + n) % n) % n;
      this.pending = true;
      const seeked = () => {
        v.removeEventListener('seeked', seeked);
        if (!this.pending) return;                                // turned again during the seek
        const shown = () => {
          if (!this.pending) { v.pause(); return; }
          this.pending = false;
          this.playing = true;
          this.el.classList.add('is-video');
        };
        const pr = v.play();
        if (pr && pr.then) pr.then(shown, () => { this.pending = false; this.noVideo = true; });   // autoplay refused
        else shown();
      };
      v.addEventListener('seeked', seeked);
      v.currentTime = i * FRAME_DT + 0.002;
      return true;
    }
    bindDrag() {
      const el = this.el;
      let x0 = null;
      let a0 = 0;
      el.addEventListener('pointerdown', (e) => {
        this.dragging = true;
        this.toFrames();
        x0 = e.clientX; a0 = this.a;
        el.classList.add('is-dragging');
        try { el.setPointerCapture(e.pointerId); } catch (_) {}
      });
      el.addEventListener('pointermove', (e) => {
        if (x0 === null) return;
        this.a = a0 - ((e.clientX - x0) / el.clientWidth) * this.n * 0.85;
      });
      const end = () => {
        if (x0 === null) return;
        x0 = null; this.dragging = false;
        this.a = Math.round(this.a);                            // settle on the whole frame it was showing
        this.hold = performance.now() + HOLD_MS;
        el.classList.remove('is-dragging');
      };
      el.addEventListener('pointerup', end);
      el.addEventListener('pointercancel', end);
      el.addEventListener('lostpointercapture', end);
    }
  }

  const spinEls = document.querySelectorAll('[data-spin]');
  if (spinEls.length) {
    const hasIO = 'IntersectionObserver' in window;
    const near = hasIO ? new IntersectionObserver((entries) => {
      for (const e of entries) {
        const s = e.target.__spin;
        s.visible = e.isIntersecting;
        if (e.isIntersecting) s.load();
      }
    }, { rootMargin: '80% 0px 80% 0px' }) : null;
    const seen = hasIO ? new IntersectionObserver((entries) => {
      for (const e of entries) {
        const s = e.target.__spin;
        s.onscreen = e.isIntersecting;
        if (!e.isIntersecting) s.toFrames();                    // off screen: no video decoding
      }
    }) : null;
    spinEls.forEach((el) => {
      const s = new Spin(el);
      el.__spin = s;
      spins.push(s);
      if (hasIO) { near.observe(el); seen.observe(el); } else { s.visible = s.onscreen = true; s.load(); }
    });

    let lastY = window.scrollY;
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      const dy = y - lastY;
      lastY = y;
      const now = performance.now();
      const perPx = 1 / (0.95 * window.innerHeight);
      for (const s of spins) {
        if (!s.visible || s.dragging) continue;
        s.toFrames();
        s.lastScroll = now;
        s.a += dy * perPx * s.n;
      }
    }, { passive: true });

    let t0 = performance.now();
    const tick = (t) => {
      const dt = Math.min(0.1, (t - t0) / 1000);
      t0 = t;
      for (const s of spins) {
        if (!s.visible) continue;
        if (s.playing) { s.a = s.video.currentTime / FRAME_DT; s.hud(s.a); continue; }
        const resting = !s.dragging && t > s.hold && t - s.lastScroll > SCROLL_REST_MS;
        if (resting && !reduce && !s.toVideo()) s.a += (s.n / IDLE_TURN_S) * dt;   // no video yet: the frames turn
        s.draw(t);
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  /* ---------------------------------------------------------------- bag */
  const Bag = {
    el: document.querySelector('[data-bag]'),
    async get() {
      const r = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      return r.json();
    },
    async refresh() { const c = await this.get(); this.render(c); return c; },
    render(c) {
      document.querySelectorAll('[data-cart-count]').forEach((n) => { n.textContent = c.item_count; });
      if (!this.el) return;
      const list = this.el.querySelector('[data-bag-lines]');
      if (!c.items.length) {
        list.innerHTML = '<p class="bag__empty mono">Your bag is empty.</p>';
      } else {
        list.innerHTML = c.items.map((it, i) => {
          const img = it.image ? it.image + (it.image.indexOf('?') > -1 ? '&' : '?') + 'width=240' : '';
          const opts = (it.options_with_values || []).filter((o) => o.value && !/^default/i.test(o.value) && (o.name || '').toLowerCase() !== 'color').map((o) => o.value).join(' / ');
          return '<div class="line" data-line="' + (i + 1) + '">' +
            (img ? '<a href="' + esc(it.url) + '"><img src="' + esc(img) + '" alt="" width="76" height="76" loading="lazy"></a>' : '<span></span>') +
            '<div class="line__txt"><a href="' + esc(it.url) + '">' + esc(it.product_title) + '</a>' +
            (opts ? '<span class="mono">' + esc(opts) + '</span>' : '') +
            '<span class="mono">' + money(it.final_line_price) + '</span></div>' +
            '<div class="line__qty"><button type="button" data-q="-1" aria-label="Remove one">−</button><span>' + it.quantity +
            '</span><button type="button" data-q="1" aria-label="Add one">+</button></div></div>';
        }).join('');
      }
      const sub = this.el.querySelector('[data-bag-subtotal]');
      if (sub) sub.textContent = money(c.total_price);
      const co = this.el.querySelector('[data-bag-checkout]');
      if (co) co.disabled = !c.item_count;
    },
    open() {
      if (!this.el) return;
      this.el.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => this.el.classList.add('is-open')));
      document.documentElement.classList.add('bag-open');
      const x = this.el.querySelector('.bag__x');
      if (x) x.focus({ preventScroll: true });
    },
    close() {
      if (!this.el) return;
      this.el.classList.remove('is-open');
      document.documentElement.classList.remove('bag-open');
      setTimeout(() => { this.el.hidden = true; }, 320);
    },
    async change(line, quantity) {
      const r = await fetch(root + 'cart/change.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      this.render(await r.json());
    }
  };

  if (Bag.el) {
    document.querySelectorAll('[data-bag-open]').forEach((a) => a.addEventListener('click', async (e) => {
      e.preventDefault();
      Bag.open();
      try { await Bag.refresh(); } catch (_) { window.location.href = a.href; }
    }));
    Bag.el.addEventListener('click', (e) => {
      if (e.target.closest('[data-bag-close]')) { Bag.close(); return; }
      const q = e.target.closest('[data-q]');
      if (q) {
        const line = Number(q.closest('[data-line]').dataset.line);
        const cur = Number(q.parentElement.querySelector('span').textContent);
        Bag.change(line, Math.max(0, cur + Number(q.dataset.q)));
      }
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !Bag.el.hidden) Bag.close(); });
    Bag.refresh().catch(() => {});
  }

  /* ---------------------------------------------------------------- product form */
  const pdp = document.querySelector('[data-product]');
  if (pdp) {
    let data = null;
    try { data = JSON.parse(pdp.querySelector('[data-product-json]').textContent); } catch (e) {}
    const form = pdp.querySelector('[data-product-form]');
    if (data && form) {
      const idIn = form.querySelector('[data-variant-id]');
      const btn = form.querySelector('[data-add]');
      const price = pdp.querySelector('[data-price]');
      const needs = form.querySelectorAll('fieldset.opt').length > 0;
      const selection = () => data.options.map((_, i) => {
        const fixed = form.querySelector('[data-fixed-option="' + i + '"]');
        if (fixed) return fixed.value;
        const r = form.querySelector('input[name="option-' + i + '"]:checked');
        return r ? r.value : null;
      });
      const find = (sel) => data.variants.find((v) => v.options.every((o, i) => o === sel[i]));
      const sync = () => {
        const sel = selection();
        if (sel.some((s) => s === null)) {
          btn.disabled = false; btn.dataset.state = 'pick'; btn.textContent = 'Select a size';
          return null;
        }
        const v = find(sel);
        if (!v) { btn.disabled = true; btn.textContent = 'Unavailable'; return null; }
        idIn.value = v.id;
        btn.dataset.state = '';
        btn.disabled = !v.available;
        btn.textContent = v.available ? 'Add to bag' : 'Sold out';
        if (price) price.textContent = money(v.price);
        const u = new URL(window.location.href);
        u.searchParams.set('variant', v.id);
        window.history.replaceState(null, '', u.toString());
        return v;
      };
      form.addEventListener('change', sync);
      if (needs) sync();
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (btn.dataset.state === 'pick') {
          const fs = form.querySelector('fieldset.opt');
          if (fs) { fs.classList.remove('nudge'); void fs.offsetWidth; fs.classList.add('nudge'); fs.querySelector('input').focus(); }
          return;
        }
        btn.disabled = true;
        btn.textContent = 'Adding…';
        try {
          const r = await fetch(root + 'cart/add.js', { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' }, body: new FormData(form) });
          const j = await r.json();
          if (!r.ok) throw j;
          await Bag.refresh();
          Bag.open();
        } catch (err) {
          btn.textContent = (err && (err.description || err.message)) || 'Try again';
          setTimeout(sync, 2200);
          return;
        }
        sync();
      });
    }
  }
})();
